import { Coordinate } from "@fairfleet/geotab";
import { AuditDevice } from "./AuditDevice";
import { MANUAL_ENTRY_SUFFIX, ReasonTaxClass } from "@/constants/ReasonTaxClasses";
import { DutyRosterRecord, TripEndRecord } from "@/hooks/addins/useManualLogbookDutyRosters";

/**
 * How the driver booked the entry: digitally (Geotab trip driven with the driver logged in
 * through the EcoApp) or analog (journey booked with a duty roster number in the EcoApp).
 */
export type RecordType = "digital" | "analog";

/** Where a fuel transaction came from: typed into the EcoApp by the driver, or imported (fuel card). */
export type RefuelSource = "app" | "import";

/**
 * A fill-up or charging session Geotab detected on its own (tank level, charging signals), as
 * MyGeotab lists them under "Erkannte Tankvorgänge" and "Ladevorgänge". Not a driver entry.
 */
export interface DetectedEvent {
  kind: "fillUp" | "charge";
  dateTime: Date;
  /** Litres for a fill-up, kWh drawn at the charger for a charging session. */
  amount: number;
  /** Where the vehicle stood, from the telematics device. */
  location?: Coordinate;
  odometerKm?: number;
  /** Geotab estimated the kWh from secondary signals. */
  estimated: boolean;
}

export interface RefuelEntry {
  dateTime: Date;
  liters: number;
  kind: "fuel" | "adBlue";
  source: RefuelSource;
  /** Geotab's provider name for imports, e.g. a fuel card company. */
  provider?: string;
  /** Where the transaction was recorded: the phone's position for EcoApp entries, the station for imports. */
  location?: Coordinate;
}

/**
 * One logbook entry. Digital entries are Geotab trips with the driver logged in, every figure
 * comes from Geotab. Analog entries are journeys the driver booked with a duty roster number,
 * every figure comes from the driver's entries in the EcoApp; the Geotab trips of such a journey
 * only lend their reason and driving time. Geotab trips nobody booked (no driver) are listed
 * as unbooked digital entries; they carry neither driver nor reason.
 */
export interface AuditTrip {
  id: string;
  device: AuditDevice;
  /** Geotab user id of the driver who booked the entry; undefined for an unbooked trip. */
  driverId?: string;
  recordType: RecordType;
  /** Digital: Geotab trip start. Analog: when the driver confirmed the duty roster. */
  start: Date;
  /** Digital: Geotab trip stop. Analog: the driver's end-of-trip entry, undefined without one. */
  stop?: Date;
  /**
   * Driving time. Digital: stop minus start. Analog: the Geotab trips inside the booking added
   * up, or without one the time between the driver's two entries; undefined without an end entry.
   */
  durationMs?: number;
  /** Analog only: true when `durationMs` is the sum of the Geotab trips of the booking. */
  durationFromGeotabTrips?: boolean;
  /**
   * Kilometres as the logbook counts them: odometer end minus odometer start (Geotab's readings
   * or the driver's); undefined when a reading is missing.
   */
  distance?: number;
  /** Digital only: the readings cannot be right (counter went backwards or faster than any vehicle). */
  odometerJump?: boolean;
  /** Digital only: `distance` includes kilometres driven without a trip record before the next trip. */
  odometerGap?: boolean;
  /** Odometer at start and end in km: Geotab's readings (digital) or the driver's entries (analog). */
  odometerStartKm?: number;
  odometerEndKm?: number;
  /** Digital only. */
  stopPoint?: Coordinate;
  /** Reason exactly as annotated, including a possible "(Manueller Eintrag)" suffix. */
  rawReason?: string;
  /** Reason without the manual-entry suffix; undefined when the trip has no annotation. */
  reason?: string;
  taxClass: ReasonTaxClass;
  /** Analog only: the roster confirmation that opened the journey and the driver's end-of-trip entry. */
  dutyRoster?: DutyRosterRecord;
  tripEnd?: TripEndRecord;
  /** Number of roster confirmations behind an analog entry (> 1 shows a hint icon). */
  dutyRosterCandidates: number;
  /** Digital only: consumption in the unit of the device's fuel type; undefined when no data. */
  consumption?: number;
  /** Digital: every fuel transaction of the trip; analog: only those the driver typed into the EcoApp. */
  refuels: RefuelEntry[];
  /**
   * Refuelled amount shown for this entry: litres of the fuel transactions (all products except
   * AdBlue); a digital entry without one falls back to Geotab's detected fill-ups (litres) or
   * charging sessions (kWh). Never both added up.
   */
  refuelLiters?: number;
  /** True when `refuelLiters` comes from `detectedEvents` instead of a transaction. */
  refuelFromDetected?: boolean;
  /** Litres of AdBlue (DieselExhaustFluid) refuelled for this entry. */
  adBlueLiters?: number;
  /** Digital only: Geotab's detected fill-ups or charging sessions between this trip's start and the next trip. */
  detectedEvents: DetectedEvent[];
}

/**
 * The reason as shown in the table and export: analog entries keep the "(Manueller Eintrag)"
 * marker so the entry reads exactly like the stored annotation. Filtering and the tax class
 * use the cleaned `reason`.
 */
export function displayReason(trip: Pick<AuditTrip, "reason" | "recordType">): string | undefined {
  if (!trip.reason) return undefined;
  return trip.recordType === "analog" ? `${trip.reason} ${MANUAL_ENTRY_SUFFIX}` : trip.reason;
}
